//Copyright (c) 2024 Greener Power Solutions.
//This file is part of Greener Power Solutions' software,
//and may not be altered, copied, redistributed, or sold
//without specific permission.
import { AlertSeverity } from 'features/alerts/alerts.types';
import { Filter } from 'features/filters/hooks/useFilter/useFilter.types';
import { ReactQueryConfig } from 'hooks/hooks.types';
import { useInfiniteAxiosQuery } from 'hooks/useInfiniteAxiosQuery';
import { useQueryParams } from 'hooks/useQueryParams';
import { EVENTS_CACHE_KEY } from 'services/queryKeys';
import { Event, PaginatedResponse } from 'types';

export function useGetEvents({
  unitId,
  reactQueryConfig = {},
  severityFilters = [],
  filters = [],
  perPage = 50,
  page,
  onPageChange,
}: {
  unitId: string;
  reactQueryConfig?: ReactQueryConfig<PaginatedResponse<Event>>;
  severityFilters?: AlertSeverity[];
  filters?: Filter[];
  perPage?: number;
  page?: number;
  onPageChange?: () => void;
}) {
  const additionalQueryParams = [
    ...(severityFilters.length ? [{ key: 'severity__in', value: severityFilters.join(',') }] : []),
    ...filters.map((i) => ({ key: i.filterKey, value: Array.isArray(i.value) ? i.value.join(',') : i.value })),
  ];

  const { queryUrl } = useQueryParams({
    key: [unitId, 'events'],
    baseUrl: `/events/${unitId}/`,
    additionalQueryParams,
    queryParamsToInclude: ['limit', 'offset'],
  });

  const {
    data = {},
    isError,
    isFetching,
    isLoading,
    hasNextPage,
    goToNextPage,
    resetPage,
    refetch,
    ...rest
  } = useInfiniteAxiosQuery<Event>({
    key: EVENTS_CACHE_KEY({ unitId, perPage, severityFilters, filters }),
    path: queryUrl,
    perPage,
    reactQueryConfig,
    ...(typeof page !== 'undefined' ? { page } : {}),
    ...(typeof onPageChange !== 'undefined' ? { onPageChange } : {}),
  });

  return {
    historicEvents: data?.accumulatedData,
    isLoading,
    isFetching,
    isError,
    hasNextPage,
    goToNextPage,
    resetPage,
    refetch,
    ...rest,
  };
}
